/**
 * TrayLineAssignDialog — full Assign Tray Line editor for a
 * single resident. Port of the WinForms
 * `frmDietech_Resident_TrayLineAssignNew` modal.
 *
 * The dialog lets the user place the resident into a specific
 * (Set, Tray) position within the facility's tray-line ordering
 * for each mealtime (Breakfast / Lunch / Dinner). Reorder
 * operations cascade — assigning the resident at tray N in a
 * set bumps everyone else in that set down by 1 (or pulls them
 * up when the resident leaves). All mutations happen client-
 * side against a working copy; Save batches the diff.
 *
 * Algorithm ports — all match the original VB logic line-for-line
 * (see frmDietech_Resident_TrayLineAssignNew.vb):
 *   • setNewTrayPosition  — main set-change handler
 *   • moveResident        — drag/↑/↓ within the same set
 *   • resequenceSet       — close gaps in tray numbers
 *
 * v1 ships with ↑/↓ buttons only; drag-and-drop deferred.
 */
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AppSelectBox from '../../components/shared/AppSelectBox';
import DialogActionsBar from '../../components/shared/DialogActionsBar';
import { SettingsAutoComplete } from '../settings/components/SettingsFormControls';
import {
  useFacilitySets,
  usePatientsForTrayLine,
  useTrayLineLocations,
  type PatientTrayLineItem,
} from '../../api/trayLineApi';
import { useSavePatientTrayLineAssignments } from '../../api/residentsApi';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useNotificationStore } from '../../stores/notificationStore';
import { useGlobalStore } from '../../stores/globalStore';
import { usePermission } from '../../hooks/usePermission';
import { assignDialogTitle, facilityUsesUnitMapping, trayLineAccess } from './trayLineRules';
import {
  useAddInteractionLog,
  INTERACTION_LOCATION,
  INTERACTION_TYPE,
} from '../../api/interactionLogApi';
import type { PatientTrayLineAssignmentRow } from '../../types/patient';
import './TrayLineAssignDialog.css';

/** Mount per opening (`{open && <TrayLineAssignDialog open ... />}`): the working copy is built once per mount. */
interface Props {
  open: boolean;
  facilityId: number;
  patientId: number;
  onClose: () => void;
  /** Opened for a resident just admitted (tray line level 2 may open it, read-only). */
  isNewResident?: boolean;
}

// 0 = Breakfast, 1 = Lunch, 2 = Dinner — same convention used
// everywhere else on the Tray Line tab.
type MealtimeId = 0 | 1 | 2;

// Working-copy row. Holds Set/Tray/Location for each of the 3
// mealtimes plus identity columns for grid rendering. Patients
// who never change stay snapshotted; only ones that diff are
// posted to the server on Save.
interface Assignment {
  pKey_Patient: number;
  fullName: string;
  firstName: string;
  lastName: string;
  room_No: string;
  bed_No: string;
  unit: string | null;
  floor: string;
  wing: string;
  set_B: number;
  set_L: number;
  set_D: number;
  tray_B: number;
  tray_L: number;
  tray_D: number;
  b_Location: string | null;
  l_Location: string | null;
  d_Location: string | null;
  discharged: boolean;
}

const MEALTIMES: MealtimeId[] = [0, 1, 2];

/** tblPatient B/L/D_Location are NVARCHAR(13). */
const LOCATION_MAX_LENGTH = 13;

// Default MUI tooltip text renders at ~11px which is hard to
// read on the dialog's compact controls. Apply the same
// 13px/relaxed-padding override the Tray Line tab uses so all
// tooltips in this dialog match.
const tooltipSlotProps = {
  tooltip: {
    sx: {
      fontSize: 13,
      lineHeight: 1.4,
      padding: '6px 10px',
    },
  },
} as const;

function setField(mt: MealtimeId): 'set_B' | 'set_L' | 'set_D' {
  return mt === 0 ? 'set_B' : mt === 1 ? 'set_L' : 'set_D';
}
function trayField(mt: MealtimeId): 'tray_B' | 'tray_L' | 'tray_D' {
  return mt === 0 ? 'tray_B' : mt === 1 ? 'tray_L' : 'tray_D';
}
function locField(mt: MealtimeId): 'b_Location' | 'l_Location' | 'd_Location' {
  return mt === 0 ? 'b_Location' : mt === 1 ? 'l_Location' : 'd_Location';
}

export default function TrayLineAssignDialog({ open, facilityId, patientId, onClose, isNewResident = false }: Props) {
  const { addNotification } = useNotificationStore();
  const facilitySettings = useGlobalStore((s) => s.facilitySettings);
  const roomLabel = useGlobalStore((s) => s.roomLabel);
  const addLog = useAddInteractionLog(facilityId);
  // Save and the move / copy / clear tools need tray line level above 2
  // (frmDietech_Resident_TrayLineAssignNew.vb:117-137). Below that the dialog
  // only shows the sets.
  const { trayLineLevel } = usePermission();
  const readOnly = !trayLineAccess(trayLineLevel, isNewResident).canSaveAssignments;

  const { data: allSets = [] } = useFacilitySets(facilityId);
  const {
    data: allPatients,
    dataUpdatedAt,
    errorUpdatedAt,
    isFetching,
    isError,
    refetch,
  } = usePatientsForTrayLine(facilityId);
  const { data: locations } = useTrayLineLocations(facilityId);
  const saveMutation = useSavePatientTrayLineAssignments(facilityId);

  // The residents are read fresh when the dialog opens (a resident just
  // admitted is not in a cached list, and other users move residents too),
  // and the working copy is built once: a background refetch must not wipe
  // edits in progress. Callers mount the dialog per opening, so this state
  // starts over each time.
  const [openedAt] = useState(() => Date.now());
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  // ── Snapshot + working copy ────────────────────────────────
  // `snapshot` is the dialog-open state used for (a) the Return
  // buttons (per-mealtime revert), (b) the close-without-save
  // diff, and (c) the post-save audit log. `draft` is the
  // currently-edited Map keyed by PKey_Patient.
  const [snapshot, setSnapshot] = useState<Map<number, Assignment>>(new Map());
  const [draft, setDraft] = useState<Map<number, Assignment>>(new Map());
  // Per-mealtime selected set (drives the grid filter + the
  // setNewTrayPosition algorithm when changed).
  const [selectedSet, setSelectedSet] = useState<{ 0: number; 1: number; 2: number }>({ 0: 0, 1: 0, 2: 0 });

  // Initialize snapshot + draft once the fresh list has arrived.
  useEffect(() => {
    if (!open || initialized || isFetching || !allPatients) return;
    if (dataUpdatedAt < openedAt) return;
    const map = new Map<number, Assignment>();
    for (const p of allPatients as PatientTrayLineItem[]) {
      map.set(p.pKey_Patient, {
        pKey_Patient: p.pKey_Patient,
        fullName: p.fullName,
        firstName: p.firstName,
        lastName: p.lastName,
        room_No: p.room_No,
        bed_No: p.bed_No,
        unit: p.unit,
        floor: p.floor,
        wing: p.wing,
        set_B: p.set_B, set_L: p.set_L, set_D: p.set_D,
        tray_B: p.tray_B, tray_L: p.tray_L, tray_D: p.tray_D,
        b_Location: p.b_Location, l_Location: p.l_Location, d_Location: p.d_Location,
        discharged: !!p.discharged,
      });
    }
    setSnapshot(map);
    // Working copy is a deep clone — never mutate the snapshot.
    setDraft(new Map(Array.from(map, ([k, v]) => [k, { ...v }])));

    const focused = map.get(patientId);
    setSelectedSet(focused
      ? { 0: focused.set_B, 1: focused.set_L, 2: focused.set_D }
      : { 0: 0, 1: 0, 2: 0 });
    setInitialized(true);
  }, [open, initialized, isFetching, allPatients, dataUpdatedAt, openedAt, patientId]);

  const loadFailed = isError && errorUpdatedAt >= openedAt && !initialized;

  // ── Mealtime + Set labels ──────────────────────────────────
  const mealtimeLabel = useCallback((mt: MealtimeId): string => {
    const fs = facilitySettings;
    switch (mt) {
      case 0: return fs?.break_Name?.trim() || 'Breakfast';
      case 1: return fs?.lunch_Name?.trim() || 'Lunch';
      case 2: return fs?.dinner_Name?.trim() || 'Dinner';
    }
  }, [facilitySettings]);

  const setNameLabel = facilitySettings?.setName?.trim() || 'Set';

  // Per-mealtime list of sets, sorted by Number, with the
  // canonical `(Unassigned)` slot at index 0.
  const setsByMealtime = useMemo(() => {
    const result = new Map<MealtimeId, Array<{ value: number; label: string }>>();
    for (const mt of MEALTIMES) {
      const rows = allSets
        .filter((s) => s.mealtime === mt)
        .sort((a, b) => a.number - b.number)
        .map((s) => {
          const trimmedName = s.name?.trim() ?? '';
          return {
            value: s.number,
            label: trimmedName ? `${trimmedName} - ${s.number}` : String(s.number),
          };
        });
      result.set(mt, [{ value: 0, label: '(Unassigned)' }, ...rows]);
    }
    return result;
  }, [allSets]);

  // ── Algorithms — direct ports of the VB logic ─────────────

  /**
   * Resequence — collapse gaps in tray numbers for one set
   * within one mealtime. Used after a resident leaves a set so
   * remaining residents are 1, 2, 3 with no holes.
   */
  const resequenceSet = useCallback((
    next: Map<number, Assignment>,
    mt: MealtimeId,
    setNumber: number,
  ) => {
    if (setNumber === 0) return;
    const sf = setField(mt);
    const tf = trayField(mt);
    const inSet = Array.from(next.values())
      .filter((p) => p[sf] === setNumber)
      .sort((a, b) => a[tf] - b[tf]);
    let i = 1;
    for (const p of inSet) {
      if (p[tf] !== i) p[tf] = i;
      i++;
    }
  }, []);

  /**
   * Core set-change handler. Mirrors WinForms `SetNewTrayPosition`:
   *   • If the resident was in a different real set before, restore
   *     all OTHER residents in that old set from the snapshot
   *     (undo any prior bumping we did to make room).
   *   • If newSet equals the resident's snapshot/default set for
   *     this mealtime: place at the snapshot tray, bump
   *     everyone with tray ≥ snapshot.tray up by 1.
   *   • If newSet === 0 (Unassigned): resident tray = 0, then
   *     resequence the OLD set tightly.
   *   • Otherwise (different real set): append at MAX(tray) + 1.
   *   • Finally, resequence the OLD set if it differed.
   */
  const setNewTrayPosition = useCallback((mt: MealtimeId, newSet: number) => {
    setDraft((prev) => {
      const next = new Map(Array.from(prev, ([k, v]) => [k, { ...v }]));
      const sf = setField(mt);
      const tf = trayField(mt);
      const focused = next.get(patientId);
      if (!focused) return next;
      const previousSet = focused[sf];

      // Restore every OTHER resident in the previous set from
      // snapshot so we start from a clean state before
      // applying the new bump.
      if (previousSet !== 0) {
        for (const p of next.values()) {
          if (p.pKey_Patient === patientId) continue;
          if (p[sf] !== previousSet) continue;
          const snap = snapshot.get(p.pKey_Patient);
          if (snap && p[tf] !== snap[tf]) p[tf] = snap[tf];
        }
      }

      const snapFocused = snapshot.get(patientId);
      const defaultSet = snapFocused?.[sf] ?? 0;
      const defaultTray = snapFocused?.[tf] ?? 0;

      if (newSet === defaultSet && newSet !== 0) {
        // Place at the resident's original tray; bump everyone
        // else with tray ≥ that value up by 1.
        focused[tf] = defaultTray;
        const toBump = Array.from(next.values())
          .filter((p) => p.pKey_Patient !== patientId && p[sf] === newSet && p[tf] >= defaultTray)
          .sort((a, b) => a[tf] - b[tf]);
        let i = defaultTray + 1;
        for (const p of toBump) {
          p[tf] = i;
          i++;
        }
      } else if (newSet === 0) {
        // Unassigned. Clear the focused tray + resequence the
        // previous set so it has no gap.
        focused[tf] = 0;
        if (previousSet !== 0) {
          // First, take focused out of the previous set logically:
          focused[sf] = 0;
          resequenceSet(next, mt, previousSet);
        }
      } else {
        // Different real set. Append at max + 1.
        const inSet = Array.from(next.values())
          .filter((p) => p.pKey_Patient !== patientId && p[sf] === newSet)
          .sort((a, b) => a[tf] - b[tf]);
        const lastTray = inSet.length === 0 ? 0 : inSet[inSet.length - 1][tf];
        focused[tf] = lastTray + 1;
      }

      focused[sf] = newSet;

      // Resequence the previous set if the focused resident left it.
      if (previousSet !== 0 && previousSet !== newSet) {
        resequenceSet(next, mt, previousSet);
      }
      return next;
    });
  }, [patientId, snapshot, resequenceSet]);

  /**
   * Move the focused resident to an arbitrary tray position
   * within their current set. Port of WinForms `MoveResident`
   * (the full version that handles ±N shifts, not just ±1).
   *
   *   • If `targetTray > currentTray`: residents in
   *     (currentTray, targetTray] shift DOWN by 1.
   *   • If `targetTray < currentTray`: residents in
   *     [targetTray, currentTray) shift UP by 1.
   *   • The focused resident lands at `targetTray`.
   *
   * Used by ↑/↓ buttons (target = current ±1) and by
   * drag-and-drop (target = drop position).
   */
  const moveResident = useCallback((mt: MealtimeId, targetTray: number) => {
    setDraft((prev) => {
      const next = new Map(Array.from(prev, ([k, v]) => [k, { ...v }]));
      const sf = setField(mt);
      const tf = trayField(mt);
      const focused = next.get(patientId);
      if (!focused) return next;
      const currentSet = focused[sf];
      if (currentSet === 0) return next;
      const currentTray = focused[tf];
      if (currentTray === targetTray) return next;
      const inSet = Array.from(next.values()).filter((p) => p[sf] === currentSet);
      const clamped = Math.max(1, Math.min(inSet.length, targetTray));
      if (currentTray > clamped) {
        // Moving Up — everyone in [clamped, currentTray) shifts DOWN by 1.
        for (const p of inSet) {
          if (p.pKey_Patient === patientId) continue;
          if (p[tf] >= clamped && p[tf] < currentTray) p[tf] = p[tf] + 1;
        }
      } else {
        // Moving Down — everyone in (currentTray, clamped] shifts UP by 1.
        for (const p of inSet) {
          if (p.pKey_Patient === patientId) continue;
          if (p[tf] > currentTray && p[tf] <= clamped) p[tf] = p[tf] - 1;
        }
      }
      focused[tf] = clamped;
      return next;
    });
  }, [patientId]);

  // ── Per-mealtime helpers (Clear / Return / Location / Copy)─

  const clearMealtime = useCallback((mt: MealtimeId) => {
    setNewTrayPosition(mt, 0);
    setSelectedSet((prev) => ({ ...prev, [mt]: 0 }));
  }, [setNewTrayPosition]);

  // Per-mealtime "Return" — reset this mealtime's set/tray for
  // EVERY resident back to their snapshot. Restores the
  // working copy to the exact state when the dialog opened
  // (only for this mealtime; other mealtimes' edits are left
  // intact). Cleaner than trying to inverse-cascade just the
  // focused-resident edits.
  const returnMealtime = useCallback((mt: MealtimeId) => {
    setDraft((prev) => {
      const next = new Map<number, Assignment>();
      const sf = setField(mt);
      const tf = trayField(mt);
      for (const [k, v] of prev) {
        const snap = snapshot.get(k);
        if (snap) {
          next.set(k, { ...v, [sf]: snap[sf], [tf]: snap[tf] });
        } else {
          next.set(k, v);
        }
      }
      return next;
    });
    const snap = snapshot.get(patientId);
    if (snap) {
      setSelectedSet((prev) => ({ ...prev, [mt]: snap[setField(mt)] }));
    }
  }, [snapshot, patientId]);

  const setLocation = useCallback((mt: MealtimeId, value: string) => {
    setDraft((prev) => {
      const next = new Map(Array.from(prev, ([k, v]) => [k, { ...v }]));
      const focused = next.get(patientId);
      if (focused) focused[locField(mt)] = value || null;
      return next;
    });
  }, [patientId]);

  const fillLocationFromBreakfast = useCallback((mt: MealtimeId) => {
    setDraft((prev) => {
      const next = new Map(Array.from(prev, ([k, v]) => [k, { ...v }]));
      const focused = next.get(patientId);
      if (focused) focused[locField(mt)] = focused.b_Location;
      return next;
    });
  }, [patientId]);

  // Copy from previous mealtime (Lunch ← Breakfast, Dinner ← Lunch).
  const copyFromPrevious = useCallback((mt: 1 | 2, mode: 'set' | 'tray' | 'both') => {
    const sourceMt = (mt - 1) as MealtimeId;
    const focused = draft.get(patientId);
    if (!focused) return;
    if (mode === 'set' || mode === 'both') {
      setNewTrayPosition(mt, focused[setField(sourceMt)]);
      setSelectedSet((prev) => ({ ...prev, [mt]: focused[setField(sourceMt)] }));
    }
    if (mode === 'tray' || mode === 'both') {
      // After the set change settles, also align the tray. The
      // setNew sets focused tray to either default or max+1 — we
      // force it to match the source mealtime's tray here, then
      // resequence the target set to absorb any gap/duplicate.
      setTimeout(() => {
        setDraft((prev) => {
          const next = new Map(Array.from(prev, ([k, v]) => [k, { ...v }]));
          const f = next.get(patientId);
          if (!f) return next;
          const targetSet = f[setField(mt)];
          if (targetSet === 0) return next;
          const targetTray = f[trayField(sourceMt)];
          if (targetTray === 0) return next;
          // Place focused at targetTray; bump everyone with tray
          // ≥ targetTray (excluding focused) up by 1.
          f[trayField(mt)] = targetTray;
          const toBump = Array.from(next.values())
            .filter((p) => p.pKey_Patient !== patientId
              && p[setField(mt)] === targetSet
              && p[trayField(mt)] >= targetTray)
            .sort((a, b) => a[trayField(mt)] - b[trayField(mt)]);
          let i = targetTray + 1;
          for (const p of toBump) {
            p[trayField(mt)] = i;
            i++;
          }
          resequenceSet(next, mt, targetSet);
          return next;
        });
      }, 0);
    }
  }, [draft, patientId, setNewTrayPosition, resequenceSet]);

  // ── Save / Cancel ─────────────────────────────────────────

  const diffRows = useMemo((): PatientTrayLineAssignmentRow[] => {
    const rows: PatientTrayLineAssignmentRow[] = [];
    for (const [pKey, current] of draft) {
      const snap = snapshot.get(pKey);
      if (!snap) continue;
      if (
        current.set_B !== snap.set_B || current.set_L !== snap.set_L || current.set_D !== snap.set_D ||
        current.tray_B !== snap.tray_B || current.tray_L !== snap.tray_L || current.tray_D !== snap.tray_D ||
        current.b_Location !== snap.b_Location || current.l_Location !== snap.l_Location || current.d_Location !== snap.d_Location
      ) {
        rows.push({
          pKey_Patient: pKey,
          setB: current.set_B, setL: current.set_L, setD: current.set_D,
          trayB: current.tray_B, trayL: current.tray_L, trayD: current.tray_D,
          b_Location: current.b_Location, l_Location: current.l_Location, d_Location: current.d_Location,
        });
      }
    }
    return rows;
  }, [draft, snapshot]);

  const dirty = diffRows.length > 0;

  const handleSave = useCallback(async () => {
    if (readOnly) return;
    try {
      await saveMutation.mutateAsync(diffRows);
      addNotification('Tray line assignments saved', 'success');
      // One Modify log per resident per mealtime that actually
      // changed — matches the WinForms loop in
      // frmDietech_Resident_TrayLineAssignNew.vb (~line 231+).
      // Note format is the WinForms string verbatim. Server-side
      // LogInteractionDAL prepends "(Web)" to each Note.
      const mealLetter: Record<MealtimeId, string> = { 0: 'B', 1: 'L', 2: 'D' };
      for (const row of diffRows) {
        const snap = snapshot.get(row.pKey_Patient);
        const after = draft.get(row.pKey_Patient);
        if (!snap || !after) continue;
        for (const mt of MEALTIMES) {
          const sf = setField(mt);
          const tf = trayField(mt);
          if (snap[sf] === after[sf] && snap[tf] === after[tf]) continue;
          const m = mealLetter[mt];
          addLog.mutate({
            fKey_Patient: row.pKey_Patient,
            fKey_Log_Interaction_Location: INTERACTION_LOCATION.TrayLine,
            fKey_Log_Interaction_Type: INTERACTION_TYPE.Modify,
            notes: `Moved From - ${m} ${setNameLabel} ${snap[sf]} Tray ${snap[tf]} To ${m} ${setNameLabel} ${after[sf]} Tray ${after[tf]}`,
          });
        }
      }
      onClose();
    } catch (err) {
      addNotification(`Error saving tray line: ${(err as Error).message}`, 'error');
    }
  }, [readOnly, saveMutation, diffRows, addNotification, snapshot, draft, setNameLabel, addLog, onClose]);

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const handleClose = useCallback(() => {
    if (dirty && !readOnly) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  }, [dirty, readOnly, onClose]);

  const confirmDiscardAndClose = useCallback(() => {
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  // ── Per-mealtime data for rendering ───────────────────────

  // Residents currently in the selected set for this mealtime,
  // sorted by tray number.
  const inSetFor = useCallback((mt: MealtimeId): Assignment[] => {
    const set = selectedSet[mt];
    if (set === 0) return [];
    const sf = setField(mt);
    const tf = trayField(mt);
    return Array.from(draft.values())
      .filter((p) => p[sf] === set)
      .sort((a, b) => a[tf] - b[tf]);
  }, [draft, selectedSet]);

  // Compute (for one mealtime) whether the focused resident's
  // tray-line position has changed from snapshot AND the
  // human-readable original-position label to show alongside
  // the Return button.
  const originalInfo = useCallback((mt: MealtimeId): {
    isChanged: boolean;
    label: string;
  } => {
    const snap = snapshot.get(patientId);
    const focused = draft.get(patientId);
    if (!snap || !focused) return { isChanged: false, label: '' };
    const sf = setField(mt);
    const tf = trayField(mt);
    const setOrig = snap[sf];
    const trayOrig = snap[tf];
    const isChanged = focused[sf] !== setOrig || focused[tf] !== trayOrig;
    if (!isChanged) return { isChanged: false, label: '' };
    if (setOrig === 0) return { isChanged: true, label: 'Unassigned' };
    // Format matches WinForms: "Original Location: {SetName} {#}, Tray {#}"
    // (see frmDietech_Resident_TrayLineAssignNew.vb:892). The
    // `setNameLabel` here is the facility-level setting name
    // (e.g. "Cart"), NOT the per-set custom name — same as VB.
    return { isChanged: true, label: `${setNameLabel} ${setOrig}, Tray ${trayOrig}` };
  }, [snapshot, draft, patientId, setNameLabel]);

  const focusedAssignment = draft.get(patientId);

  // Floor/Wing columns instead of Unit when the facility doesn't map units
  // (frmDietech_Resident_TrayLineAssignNew.Fill_dtPatient).
  const useUnitMapping = facilityUsesUnitMapping(
    facilitySettings,
    (allPatients ?? []).some((p) => !!p.unit?.trim()),
  );

  // The facility's locations for the meal (WinForms Fill_BLDLocations), plus
  // the resident's own so a typed one still shows.
  const locationOptions = (mt: MealtimeId): string[] => {
    const list = (mt === 0 ? locations?.b : mt === 1 ? locations?.l : locations?.d) ?? [];
    const own = focusedAssignment?.[locField(mt)]?.trim();
    return own && !list.some((l) => l === own) ? [...list, own].sort((a, b) => a.localeCompare(b)) : list;
  };

  if (!open) return null;

  // WinForms caption (ucDietech_Resident_TrayLineOverride.ShowTrayLineAssign),
  // with the facility's labels: "... for LAST, FIRST - Unit/Room/Bed: U/R/B",
  // or Floor/Wing/Room/Bed when the facility doesn't map units.
  const title = focusedAssignment
    ? assignDialogTitle({
      fullName: focusedAssignment.fullName,
      useUnitMapping,
      unit: focusedAssignment.unit,
      floor: focusedAssignment.floor,
      wing: focusedAssignment.wing,
      room: focusedAssignment.room_No,
      bed: focusedAssignment.bed_No,
      labels: {
        unit: 'Unit',
        floor: 'Floor',
        wing: facilitySettings?.wing?.trim() || 'Wing',
        room: roomLabel || 'Room',
        bed: 'Bed',
      },
    })
    : 'Tray Line Assignment';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            height: '92vh',
            maxHeight: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1, py: 1 }}>
        <span style={{ flex: 1, fontSize: 14 }}>
          {title}
        </span>
        <Tooltip title="Close" slotProps={tooltipSlotProps}>
          <IconButton size="small" onClick={handleClose}>
            <i className="bi bi-x-lg" style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent
        dividers
        className="tla-dialog-content"
        sx={{ flex: 1, p: 1.5, overflow: 'hidden' }}
      >
        {loadFailed ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            The residents could not be loaded. Close the dialog and try again.
          </div>
        ) : !initialized ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading facility patients…
          </div>
        ) : (
          <div className="tla-row-strip">
            {MEALTIMES.map((mt) => {
              const orig = originalInfo(mt);
              return (
                <MealtimeSection
                  key={mt}
                  mt={mt}
                  label={mealtimeLabel(mt)}
                  setNameLabel={setNameLabel}
                  setOptions={setsByMealtime.get(mt) ?? []}
                  selectedSet={selectedSet[mt]}
                  onSetChange={(v) => {
                    // Read-only: the set picker only browses (WinForms
                    // cmbBSet_SelectedIndexChanged below level 2).
                    if (!readOnly) setNewTrayPosition(mt, v);
                    setSelectedSet((prev) => ({ ...prev, [mt]: v }));
                  }}
                  readOnly={readOnly}
                  useUnitMapping={useUnitMapping}
                  locationOptions={locationOptions(mt)}
                  onClear={() => clearMealtime(mt)}
                  onReturn={() => returnMealtime(mt)}
                  onMoveUp={() => {
                    const f = draft.get(patientId);
                    if (f) moveResident(mt, f[trayField(mt)] - 1);
                  }}
                  onMoveDown={() => {
                    const f = draft.get(patientId);
                    if (f) moveResident(mt, f[trayField(mt)] + 1);
                  }}
                  onMoveToTray={(target) => moveResident(mt, target)}
                  inSet={inSetFor(mt)}
                  focused={focusedAssignment}
                  location={focusedAssignment?.[locField(mt)] ?? ''}
                  onLocationChange={(v) => setLocation(mt, v)}
                  onFillLocFromBreakfast={mt > 0 ? () => fillLocationFromBreakfast(mt) : undefined}
                  breakfastLocation={focusedAssignment?.b_Location ?? ''}
                  onCopyFromPrevious={
                    mt === 1 || mt === 2
                      ? (mode) => copyFromPrevious(mt, mode)
                      : undefined
                  }
                  previousMealtimeLabel={mt > 0 ? mealtimeLabel((mt - 1) as MealtimeId) : ''}
                  isChangedFromOriginal={orig.isChanged}
                  originalLabel={orig.label}
                />
              );
            })}
          </div>
        )}
      </DialogContent>
      {/* Read-only (no tray line save rights): nothing to save, so Close only. */}
      <DialogActionsBar
        onCancel={handleClose}
        cancelLabel={readOnly ? 'Close' : 'Cancel'}
        onSave={readOnly ? undefined : () => void handleSave()}
        savingLabel="Saving…"
        saveDisabled={!dirty}
        saving={saveMutation.isPending}
      />

      <ConfirmDialog
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={confirmDiscardAndClose}
        title="Discard Changes"
        message="You have unsaved tray line changes. Discard them and close?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="warning"
      />
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Per-mealtime column. Receives the resident's working values
// + callbacks for every action; owns no state itself.

interface MealtimeSectionProps {
  mt: MealtimeId;
  label: string;
  setNameLabel: string;
  setOptions: Array<{ value: number; label: string }>;
  selectedSet: number;
  onSetChange: (v: number) => void;
  onClear: () => void;
  onReturn: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onMoveToTray: (targetTray: number) => void;
  inSet: Assignment[];
  focused: Assignment | undefined;
  location: string;
  onLocationChange: (v: string) => void;
  onFillLocFromBreakfast?: () => void;
  breakfastLocation: string;
  onCopyFromPrevious?: (mode: 'set' | 'tray' | 'both') => void;
  previousMealtimeLabel: string;
  isChangedFromOriginal: boolean;
  originalLabel: string;
  /** View only: no moving, copying, clearing or location edits. */
  readOnly: boolean;
  /** Unit column when the facility maps units, else Floor and Wing. */
  useUnitMapping: boolean;
  /** The facility's locations for this meal. */
  locationOptions: string[];
}

function MealtimeSection({
  mt,
  label,
  setNameLabel,
  setOptions,
  selectedSet,
  onSetChange,
  onClear,
  onReturn,
  onMoveUp,
  onMoveDown,
  onMoveToTray,
  inSet,
  focused,
  location,
  onLocationChange,
  onFillLocFromBreakfast,
  breakfastLocation,
  onCopyFromPrevious,
  previousMealtimeLabel,
  isChangedFromOriginal,
  originalLabel,
  readOnly,
  useUnitMapping,
  locationOptions,
}: MealtimeSectionProps) {
  const [copyAnchor, setCopyAnchor] = useState<HTMLElement | null>(null);
  const canCopyLoc = !!(breakfastLocation && breakfastLocation.trim());

  // Scroll the focused row into view whenever the selected
  // set changes (or the focused resident's position in that
  // set shifts after ↑/↓). `block: 'nearest'` keeps the scroll
  // local to the grid wrapper instead of jumping the dialog.
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const focusedPKey = focused?.pKey_Patient;
  useEffect(() => {
    if (selectedSet === 0) return;
    const wrapper = gridWrapperRef.current;
    if (!wrapper) return;
    const row = wrapper.querySelector<HTMLTableRowElement>('tr.tla-row-focused');
    if (row) row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedSet, focusedPKey, inSet]);

  // ↑↓ enable rules. The buttons act on the FOCUSED resident
  // within their own current set for this mealtime. So:
  //   • If the user is browsing a different set than the
  //     focused resident's set, both arrows are disabled
  //     (clicking would mutate a set the user can't see).
  //   • Otherwise, ↑ disabled at top of the set, ↓ disabled
  //     at the bottom of the set.
  // Declared BEFORE the drag-and-drop block because the drag
  // handlers' `focusedInSelectedSet` depends on focusedIdxInSet
  // (per memory: hook deps evaluate top-down at render — TDZ
  // throws if you reference a let/const declared further down).
  const focusedSet = focused
    ? (mt === 0 ? focused.set_B : mt === 1 ? focused.set_L : focused.set_D)
    : 0;
  const focusedIdxInSet =
    focused && selectedSet === focusedSet && focusedSet > 0
      ? inSet.findIndex((p) => p.pKey_Patient === focused.pKey_Patient)
      : -1;
  const canMoveUp = !readOnly && focusedIdxInSet > 0;
  const canMoveDown = !readOnly && focusedIdxInSet >= 0 && focusedIdxInSet < inSet.length - 1;

  // Drag-and-drop state — only the focused resident is draggable
  // (matches WinForms: drag only moves the saving patient).
  // `dropTargetTray` is the tray number where the focused
  // resident would land if dropped now; null when not over a
  // valid target. We render a horizontal indicator line on the
  // top of the row matching that tray.
  const [dropTargetTray, setDropTargetTray] = useState<number | null>(null);
  const draggingRef = useRef(false);
  const focusedTray = focusedPKey
    ? (mt === 0 ? focused?.tray_B : mt === 1 ? focused?.tray_L : focused?.tray_D) ?? 0
    : 0;
  const focusedInSelectedSet = focusedIdxInSet >= 0;

  const handleRowDragStart = useCallback((e: React.DragEvent<HTMLTableRowElement>) => {
    // Only allow dragging the focused resident's own row.
    draggingRef.current = true;
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require some data to register a drag.
    e.dataTransfer.setData('text/plain', 'tla-row');
  }, []);

  const handleRowDragEnd = useCallback(() => {
    draggingRef.current = false;
    setDropTargetTray(null);
  }, []);

  const handleRowDragOver = useCallback((
    e: React.DragEvent<HTMLTableRowElement>,
    rowTray: number,
  ) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Determine if drop target is BEFORE this row (upper half)
    // or AFTER it (lower half). Matches WinForms' hitInfo
    // upper/lower-half check.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const isUpperHalf = (e.clientY - rect.top) < rect.height / 2;
    const target = isUpperHalf ? rowTray : rowTray + 1;
    if (dropTargetTray !== target) setDropTargetTray(target);
  }, [dropTargetTray]);

  const handleRowDrop = useCallback((
    e: React.DragEvent<HTMLTableRowElement>,
    rowTray: number,
  ) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const isUpperHalf = (e.clientY - rect.top) < rect.height / 2;
    // insertPos = the gap-position where the row would be
    // inserted; 1..length+1. Convert to final tray taking into
    // account that the focused row vacates its slot first.
    const insertPos = isUpperHalf ? rowTray : rowTray + 1;
    let target: number;
    if (insertPos < focusedTray) target = insertPos;
    else if (insertPos > focusedTray + 1) target = insertPos - 1;
    else target = focusedTray; // dropping into own slot — no-op
    setDropTargetTray(null);
    draggingRef.current = false;
    if (target !== focusedTray) onMoveToTray(target);
  }, [focusedTray, onMoveToTray]);

  // Auto-scroll when dragging near the grid wrapper's top/bottom
  // edges — without this, dropping past the visible region
  // requires manual scroll. Mirrors WinForms `XtraGrid_DragDropScroll`.
  const handleWrapperDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    const wrapper = e.currentTarget;
    const rect = wrapper.getBoundingClientRect();
    const margin = 32;
    if (e.clientY - rect.top < margin) wrapper.scrollTop -= 8;
    else if (rect.bottom - e.clientY < margin) wrapper.scrollTop += 8;
  }, []);


  return (
    <div className="tla-section">
      {/* Header — two-row label/control block, matches WinForms.
            Row 1: "{Meal} {SetName}:" | Set spin | Copy split (L/D)  | ↑
            Row 2: "Location:"         | text input | copy-from-B (L/D) | ↓ */}
      <div className="tla-section-header">
        <div className="tla-header-rows">
          <div className="tla-header-row">
            <label className="tla-header-label">{label} {setNameLabel}:</label>
            <div className="tla-header-control">
              <SetSpin
                ariaLabel={`${label} ${setNameLabel}`}
                options={setOptions}
                value={selectedSet}
                onChange={onSetChange}
              />
            </div>
            {onCopyFromPrevious && !readOnly && (
              <>
                <Tooltip title={`Copy from ${previousMealtimeLabel}`} slotProps={tooltipSlotProps}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => setCopyAnchor(e.currentTarget)}
                      className="tla-icon-btn"
                    >
                      <i className="bi bi-copy" style={{ fontSize: 15 }} />
                      <i className="bi bi-caret-down-fill" style={{ fontSize: 10, marginLeft: 2 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Menu anchorEl={copyAnchor} open={Boolean(copyAnchor)} onClose={() => setCopyAnchor(null)}>
                  <MenuItem onClick={() => { onCopyFromPrevious('both'); setCopyAnchor(null); }}>
                    Copy {setNameLabel} and Tray from {previousMealtimeLabel}
                  </MenuItem>
                  <MenuItem onClick={() => { onCopyFromPrevious('set'); setCopyAnchor(null); }}>
                    Copy {setNameLabel} from {previousMealtimeLabel}
                  </MenuItem>
                  <MenuItem
                    onClick={() => { onCopyFromPrevious('tray'); setCopyAnchor(null); }}
                    disabled={selectedSet === 0}
                  >
                    Copy Tray from {previousMealtimeLabel}
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>
          <div className="tla-header-row">
            <label className="tla-header-label">Location:</label>
            <div className="tla-header-control">
              <LocationPicker
                value={location}
                options={locationOptions}
                readOnly={readOnly}
                ariaLabel={`${label} location`}
                onChange={onLocationChange}
              />
            </div>
            {onFillLocFromBreakfast && !readOnly && (
              <Tooltip title="Copy from Breakfast" slotProps={tooltipSlotProps}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onFillLocFromBreakfast}
                    disabled={!canCopyLoc}
                    className="tla-icon-btn"
                  >
                    <i className="bi bi-copy" style={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {/* ↑↓ stacked inside the section header, on the right —
              aligned with the two header rows. Styled like the
              project's outlined toolbar buttons (same `Button` +
              `app-btn-icon` pattern used by the tab toolbar). */}
        {!readOnly && (
        <div className="tla-section-arrows">
          <Tooltip title={`Move ${focused?.fullName ?? 'resident'} up`} slotProps={tooltipSlotProps}>
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={onMoveUp}
                disabled={!canMoveUp}
                className="tla-arrow-btn"
              >
                <i className="bi bi-arrow-up app-btn-icon" />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={`Move ${focused?.fullName ?? 'resident'} down`} slotProps={tooltipSlotProps}>
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={onMoveDown}
                disabled={!canMoveDown}
                className="tla-arrow-btn"
              >
                <i className="bi bi-arrow-down app-btn-icon" />
              </Button>
            </span>
          </Tooltip>
        </div>
        )}
      </div>

        {/* Resident grid — fills the rest of the section vertically.
              The grid-wrapper hosts the auto-scroll-on-drag handler. */}
        <div
          className="tla-grid-wrapper"
          ref={gridWrapperRef}
          onDragOver={handleWrapperDragOver}
        >
          {selectedSet === 0 ? (
            <div className="tla-grid-empty">
              Select a {setNameLabel} to Assign Location
            </div>
          ) : (
            <table className="tla-grid">
              <thead>
                <tr>
                  {useUnitMapping ? (
                    <th className="tla-col-unit">Unit</th>
                  ) : (
                    <>
                      <th className="tla-col-floor">Fl.</th>
                      <th className="tla-col-wing">Wing</th>
                    </>
                  )}
                  <th className="tla-col-room">Room</th>
                  <th>Assigned Residents</th>
                  <th className="tla-col-tray">T</th>
                </tr>
              </thead>
              <tbody>
                {inSet.map((p, idx) => {
                  const isFocused = focused && p.pKey_Patient === focused.pKey_Patient;
                  const tray = mt === 0 ? p.tray_B : mt === 1 ? p.tray_L : p.tray_D;
                  const isLastRow = idx === inSet.length - 1;
                  // Drop indicator gap:
                  //   • dropTargetTray === T  → indicator BEFORE row tray T
                  //   • dropTargetTray === lastTray + 1 → indicator AFTER last row
                  // (The gap that lands inside the focused row's
                  // own slot is suppressed below as a no-op.)
                  const noOpGap =
                    dropTargetTray === focusedTray || dropTargetTray === focusedTray + 1;
                  const indicatorActive = focusedInSelectedSet && !noOpGap;
                  const showDropAbove = indicatorActive && dropTargetTray === tray;
                  const showDropBelow =
                    indicatorActive && isLastRow && dropTargetTray !== null && dropTargetTray > tray;
                  const classes = [
                    isFocused ? 'tla-row-focused' : '',
                    showDropAbove ? 'tla-row-drop-above' : '',
                    showDropBelow ? 'tla-row-drop-below' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <tr
                      key={p.pKey_Patient}
                      className={classes || undefined}
                      draggable={!!isFocused && !readOnly}
                      onDragStart={isFocused && !readOnly ? handleRowDragStart : undefined}
                      onDragEnd={isFocused && !readOnly ? handleRowDragEnd : undefined}
                      onDragOver={(e) => handleRowDragOver(e, tray)}
                      onDrop={(e) => handleRowDrop(e, tray)}
                      style={isFocused && !readOnly ? { cursor: 'grab' } : undefined}
                    >
                      {useUnitMapping ? (
                        <td className="tla-col-unit">{p.unit?.trim() || ''}</td>
                      ) : (
                        <>
                          <td className="tla-col-floor">{p.floor?.trim() || ''}</td>
                          <td className="tla-col-wing">{p.wing?.trim() || ''}</td>
                        </>
                      )}
                      <td className="tla-col-room">
                        {p.room_No}{p.bed_No ? `/${p.bed_No}` : ''}
                      </td>
                      <td>
                        {p.fullName}
                        {p.discharged && (
                          <span className="tla-discharged" title="Discharged: still holds this tray">Discharged</span>
                        )}
                      </td>
                      <td className="tla-col-tray">{tray}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer — Clear on the left, an "Original Location"
              hint + Return button on the right that appears
              ONLY when the focused resident's tray-line position
              has been edited away from the snapshot. */}
        <div className="tla-section-footer">
          {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            onClick={onClear}
            disabled={selectedSet === 0}
            sx={{ textTransform: 'none', fontSize: 'var(--font-size-small)', minHeight: 26, py: 0, px: 1.5 }}
          >
            Clear
          </Button>
          )}
          {isChangedFromOriginal && !readOnly && (
            <div className="tla-original-position">
              <span className="tla-original-text">
                Original Location:{' '}
                <span className="tla-original-value">{originalLabel}</span>
              </span>
              <Tooltip title="Return to original" slotProps={tooltipSlotProps}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onReturn}
                    className="tla-return-btn"
                  >
                    <i className="bi bi-arrow-counterclockwise app-btn-icon" />
                  </Button>
                </span>
              </Tooltip>
            </div>
          )}
        </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Set picker — matches the same shell used on the per-day
// override popover (a dropdown sandwiched between −/+ spin
// buttons). Stepping through the option list mirrors the
// WinForms cmbSet button-click behavior.

interface SetSpinProps {
  /** The caption beside the shell, without its colon ("Breakfast Set"). */
  ariaLabel: string;
  options: Array<{ value: number; label: string }>;
  value: number;
  onChange: (v: number) => void;
}

function SetSpin({ ariaLabel, options, value, onChange }: SetSpinProps) {
  const currentIdx = options.findIndex((o) => o.value === value);
  const apply = (idx: number) => {
    const clamped = Math.max(0, Math.min(options.length - 1, idx));
    onChange(options[clamped].value);
  };
  return (
    <div className="settings-spin-edit tla-set-spin">
      <button
        type="button"
        className="settings-spin-btn"
        onClick={() => apply(currentIdx - 1)}
        disabled={currentIdx <= 0}
        aria-label="Previous set"
      >
        <i className="bi bi-dash" />
      </button>
      <div className="tla-set-spin-input">
        {/* Reports only a pick from the list, never the dialog setting
            `value` (seeding on open, Return, Clear, Copy); the -/+ buttons
            call onChange themselves. */}
        <AppSelectBox
          dataSource={options}
          displayExpr="label"
          valueExpr="value"
          value={value}
          ariaLabel={ariaLabel}
          onValueChanged={(v) => onChange(typeof v === 'number' ? v : 0)}
          showClearButton={false}
          height={32}
        />
      </div>
      <button
        type="button"
        className="settings-spin-btn"
        onClick={() => apply(currentIdx + 1)}
        disabled={currentIdx >= options.length - 1}
        aria-label="Next set"
      >
        <i className="bi bi-plus" />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Location picker: the facility's locations for the meal, and a new one can
// be typed (WinForms cmb_B_Location, a ComboBoxEdit filled by
// Fill_BLDLocations). Text is kept as typed; the column holds 13 characters.

interface LocationPickerProps {
  value: string;
  options: string[];
  readOnly: boolean;
  ariaLabel: string;
  onChange: (v: string) => void;
}

function LocationPicker({ value, options, readOnly, ariaLabel, onChange }: LocationPickerProps) {
  // Free text with the facility's locations as suggestions. The text is
  // reported as typed (cut to the column's 13 characters) and trimmed when
  // the field is left, as the DevExtreme box trimmed a typed location.
  return (
    <div
      className="tla-loc-picker"
      onBlur={() => {
        const trimmed = (value ?? '').trim();
        if (trimmed !== (value ?? '')) onChange(trimmed);
      }}
    >
      <SettingsAutoComplete
        label=""
        srLabel={ariaLabel}
        value={value ?? ''}
        suggestions={options}
        onChange={onChange}
        maxLength={LOCATION_MAX_LENGTH}
        placeholder="(Optional)"
        clearable={!readOnly}
        disabled={readOnly}
      />
    </div>
  );
}
